import { createRootRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { Show, useAuth, useUser } from "@clerk/react";
import { User } from "lucide-react";
import { cn } from "@/lib/utils";
import { authFetch } from "@/lib/api";
import { track } from "@/lib/analytics";
import "../index.css";

export const Route = createRootRoute({
  component: RootLayout,
});

type RouteGroup =
  | "home"
  | "records"
  | "athlete"
  | "event"
  | "series"
  | "result"
  | "compare"
  | "myfirstmarathon"
  | "content";

function routeGroupForPath(pathname: string): RouteGroup {
  if (pathname === "/") return "home";
  if (pathname === "/records" || pathname.startsWith("/records/")) return "records";
  if (pathname.startsWith("/athletes/")) return "athlete";
  if (pathname.startsWith("/events/series/")) return "series";
  if (pathname.startsWith("/events/")) return "event";
  if (pathname.startsWith("/results/")) return "result";
  if (pathname === "/compare" || pathname.startsWith("/compare")) return "compare";
  if (pathname === "/myfirstmarathon" || pathname.startsWith("/myfirstmarathon/")) {
    return "myfirstmarathon";
  }
  return "content";
}

function RootLayout() {
  const [revealed, setRevealed] = useState(false);
  const location = useRouterState({
    select: (state) => ({
      pathname: state.location.pathname,
      search: state.location.search,
    }),
  });
  const pathname = location.pathname;
  const searchKey = JSON.stringify(location.search ?? {});
  const previousPathRef = useRef<string | null>(null);

  useEffect(() => {
    requestAnimationFrame(() => setRevealed(true));
  }, []);

  useEffect(() => {
    const fullPath =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : pathname;
    const referrer =
      previousPathRef.current ??
      (typeof document !== "undefined" ? document.referrer || null : null);

    track("tr_page_view", {
      path: pathname,
      full_path: fullPath,
      route_group: routeGroupForPath(pathname),
      referrer,
    });

    previousPathRef.current = fullPath;
  }, [pathname, searchKey]);

  const isSearchSection =
    pathname === "/records" ||
    pathname.startsWith("/athletes/") ||
    pathname.startsWith("/events/") ||
    pathname.startsWith("/results/");
  const isMyFirstMarathon =
    pathname === "/myfirstmarathon" || pathname.startsWith("/myfirstmarathon/");

  if (isMyFirstMarathon) {
    return <Outlet />;
  }

  return (
    <div
      className="relative min-h-screen overflow-hidden bg-background text-foreground"
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      <div className="d8-mesh" />
      <div className="d8-grain" />
      <div className="d8-orb d8-orb-1" />
      <div className="d8-orb d8-orb-2" />

      <div className="d8-content">
        <div className={cn("d8-topbar", revealed && "revealed")}>
          <Link to="/" search={{}} className="d8-logo">
            TOMORROW'S RUN
          </Link>
          <div className="d8-nav-pills">
            <Link
              to="/records"
              search={{}}
              className={cn("d8-pill", isSearchSection && "active")}
            >
              Search
            </Link>
            <Link
              to="/compare"
              search={{}}
              className={cn("d8-pill", pathname === "/compare" && "active")}
            >
              Compare
            </Link>
            <Link to="/about" className={cn("d8-pill", pathname === "/about" && "active")}>
              About
            </Link>
            <Link
              to="/changelog"
              className={cn("d8-pill", pathname === "/changelog" && "active")}
            >
              Changelog
            </Link>
            <Link
              to="/support"
              className={cn("d8-pill", pathname === "/support" && "active")}
            >
              Support
            </Link>
            <Link
              to="/newsletter"
              className={cn("d8-pill", pathname === "/newsletter" && "active")}
            >
              Newsletter
            </Link>
          </div>
          <div className="d8-auth">
            <Show when="signed-out">
              <Link to="/sign-in" className="d8-pill">
                Sign in
              </Link>
            </Show>
            <Show when="signed-in">
              <UserSync />
              <ProfileButton />
            </Show>
          </div>
        </div>

        <main className="d8-main">
          <Outlet />
        </main>

        <footer className={cn("d8-footer", revealed && "revealed")}>
          <div className="d8-footer-links">
            <Link to="/terms-of-service" className="d8-footer-link">
              Terms of Service
            </Link>
            <Link to="/privacy-policy" className="d8-footer-link">
              Privacy Policy
            </Link>
            <Link to="/support" className="d8-footer-link">
              Support
            </Link>
            <Link to="/newsletter" className="d8-footer-link">
              Newsletter
            </Link>
          </div>

          <button
            type="button"
            className="d8-footer-x-placeholder"
            disabled
            aria-label="Connect on X (coming soon)"
            title="X profile coming soon"
          >
            <SocialXIcon />
          </button>
        </footer>
      </div>
    </div>
  );
}

function UserSync() {
  const { isSignedIn, getToken } = useAuth();
  const hasSynced = useRef(false);

  useEffect(() => {
    if (!isSignedIn || hasSynced.current) return;
    hasSynced.current = true;
    (async () => {
      try {
        const token = await getToken();
        await authFetch("/users/sync", { method: "POST", token });
      } catch {
        hasSynced.current = false;
      }
    })();
  }, [isSignedIn, getToken]);

  return null;
}

function ProfileButton() {
  const { user } = useUser();

  return (
    <Link to="/profile" className="d8-profile-btn" aria-label="Profile">
      {user?.imageUrl ? (
        <img src={user.imageUrl} alt="" className="d8-profile-img" />
      ) : (
        <User className="d8-profile-icon" />
      )}
    </Link>
  );
}

function SocialXIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="currentColor"
    >
      <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.847h-7.406l-5.8-7.584-6.639 7.584H.47l8.6-9.83L0 1.154h7.594l5.243 6.932zM17.61 20.645h2.039L6.486 3.24H4.298z" />
    </svg>
  );
}
